import { Table } from 'antd';
import { FC, useMemo, useState } from 'react';
import Collapse from '@dynamika-shared/ui/Collapse/Collapse';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { twJoin } from '@dynamika-shared/utils/twMerge';
import { QCTReading } from '@dynamika-shared/types/QCTReading';
import { Label } from '@restore/clinicalTrialContext/clinicalTrialContext.types';
import { CollapseHeader, CollapseState } from '../components/CollapseHeader';
import { makeColumns } from './Table.columns';

export type ReadingsTableProps = {
  onRemove: (reading: QCTReading) => void;
  /** Move a saved result onto another label. Un-labelling is Remove. */
  onLabelChange: (reading: QCTReading, labelId: string) => void;
  /** Study QCT labels not yet assigned anywhere in this visit. */
  labels: Label[];
  readings: QCTReading[];
  loading: boolean;
  readOnly: boolean;
};

export const ReadingsTable: FC<ReadingsTableProps> = ({
  onRemove,
  onLabelChange,
  labels,
  readings,
  loading,
  readOnly,
}) => {
  const [collapseValue, setCollapseValue] = useState<CollapseState | null>(
    null
  );

  const [valueToRemove, setValueToRemove] = useState<QCTReading | null>(null);

  /**
   * How much of the list is showing.
   *
   * Plain terms
   * -----------
   * The section opens itself as soon as there is something to read, and stays
   * open or shut wherever the specialist last put it once they have touched
   * it.
   *
   * Technical
   * ---------
   * Derived rather than pushed into state from a `useMemo` body: a `setState`
   * inside `useMemo` is not guaranteed to run once per commit — StrictMode
   * double-invokes it and concurrent rendering may discard the result. `null`
   * means "never toggled", so the first rows to arrive open the list.
   */
  const collapseState: CollapseState =
    collapseValue ?? (readings.length ? 'open' : 'closed');

  const handleRemove = () => {
    onRemove(valueToRemove as QCTReading);
    setValueToRemove(null);
  };

  const columns = useMemo(
    () =>
      makeColumns({
        mode: 'readings',
        onRemove: setValueToRemove,
        onLabelChange,
        availableLabels: labels,
        readOnly,
      }),
    [onLabelChange, labels, readOnly]
  );

  const sortedReadings = useMemo(
    () =>
      [...readings].sort(
        (a, b) => a.labelName?.localeCompare(b.labelName || '') || 0
      ),
    [readings]
  );

  return (
    <div>
      <h3 className='text-[2.4rem] leading-[3.2rem] font-semibold my-4'>
        Selected for Reading
      </h3>

      <Collapse
        open={collapseState !== 'closed'}
        hideChevron
        notUseInnerState
        noAnimation
        noRender
        onClick={() =>
          setCollapseValue(collapseState === 'closed' ? 'open' : 'closed')
        }
        label={
          <CollapseHeader
            collapseValue={collapseState}
            title={'Added Results'}
          />
        }
      >
        <div
          id='QCT_Added_Readings'
          data-testid='QCTSelectedForReading'
          className={twJoin(
            'overflow-auto',
            collapseState === 'open' && 'max-h-[20rem]'
          )}
        >
          <Table
            rowKey='rowKey'
            locale={{
              emptyText: (
                <NoContent
                  iconName={'noContent'}
                  title={
                    <span className='text-spanishGray'>
                      No values were selected for{' '}
                      <b className='text-black'>Reading</b> yet.
                      <br />
                      Retrieve results from the QCT workstation, if not done
                      yet, and continue selecting the values.
                    </span>
                  }
                >
                  {' '}
                </NoContent>
              ),
            }}
            loading={loading}
            pagination={false}
            columns={columns}
            dataSource={sortedReadings}
          />
        </div>
      </Collapse>
      <Modal
        width={440}
        title={<div className='w-full flex'>Remove</div>}
        onOk={handleRemove}
        onCancel={() => setValueToRemove(null)}
        okText='Remove'
        centered
        okClassName='ant-btn-dangerous'
        open={!!valueToRemove}
      >
        Are you sure you want to remove this result from the selected list?
      </Modal>
    </div>
  );
};
